// ─── Constants ──────────────────────────────────────────────────────────────
export const VIEW_MODE_OPTIONS = [
  { key: 'ad', label: 'Ads' },
  { key: 'theme', label: 'Themes' },
  { key: 'subtheme', label: 'Subthemes' },
];

export const CLIENT_FIELDS = [
  'id',
  'name',
  'slug',
  'parent_company',
  'parent_company_name',
  'mapping_types',
].join(',');

export const MAX_CLIENT_META_PAGES = 50;
export const RECENT_WINDOW_DAYS = 14;
export const COHORT_MATURITY_LAG_DAYS = 7;
export const MIN_PEER_COUNT = 3;
export const MIN_IMPRESSIONS_FOR_ACTION = 5000;

export const VIDEO_FORMAT_TOKENS = ['VIDEO', 'REEL', 'STORY', 'MOTION', 'ANIM'];
export const STATIC_FORMAT_TOKENS = ['STATIC', 'IMAGE', 'PHOTO', 'STILL', 'SINGLE IMAGE'];
export const DYNAMIC_FORMAT_TOKENS = ['DYNAMIC', 'CATALOG', 'DPA', 'ADVANTAGE+'];
export const CAROUSEL_FORMAT_TOKENS = ['CAROUSEL', 'COLLECTION'];

export const COM_CONFIDENCE_NOT_ELIGIBLE = 'Not Eligible';
export const COM_CONFIDENCE_EARLY = 'Early CoM';
export const COM_CONFIDENCE_DEVELOPING = 'Developing CoM';
export const COM_CONFIDENCE_STABLE = 'Stable CoM';

export const DEFAULT_CREATIVE_TARGETS = {
  ctr: 0.0125,
  lpvRate: 0.45,
  hookRate: 0.25,
  thruplayRate: 0.06,
  holdRate50: 0.3,
};

export const DEFAULT_BUSINESS_TARGETS = {
  cpl: 200,
  cpSelfBook: 400,
  cpScheduled: 600,
  cpIssue: 1000,
  cpDemo: 2500,
  costOfMarketing: 0.2,
};

export const METRIC_COLUMNS = [
  { key: 'spend', label: 'Spend', type: 'currency' },
  { key: 'hookRate', label: 'Hook Rate', type: 'ratio' },
  { key: 'ctr', label: 'CTR', type: 'ratio' },
  { key: 'lpvRate', label: 'LPV Rate', type: 'ratio' },
  { key: 'thruplayRate', label: 'Thruplay Rate', type: 'ratio' },
  { key: 'holdRate25', label: 'Hold 25%', type: 'ratio' },
  { key: 'holdRate50', label: 'Hold 50%', type: 'ratio' },
  { key: 'holdRate75', label: 'Hold 75%', type: 'ratio' },
  { key: 'holdRate100', label: 'Hold 100%', type: 'ratio' },
  { key: 'leads', label: 'Leads', type: 'count' },
  { key: 'cpl', label: 'CPL', type: 'currency' },
  { key: 'selfBook', label: 'Self Book', type: 'count' },
  { key: 'cpSelfBook', label: 'CP Self Book', type: 'currency' },
  { key: 'booked', label: 'Booked', type: 'count' },
  { key: 'cpBooked', label: 'CP Booked', type: 'currency' },
  { key: 'setRate', label: 'Set Rate', type: 'ratio' },
  { key: 'scheduled', label: 'Appointments', type: 'count' },
  { key: 'cpScheduled', label: 'CP Appointment', type: 'currency' },
  { key: 'issues', label: 'Issues', type: 'count' },
  { key: 'cpIssue', label: 'CP Issue', type: 'currency' },
  { key: 'issueRate', label: 'Issue Rate', type: 'ratio' },
  { key: 'leadToIssue', label: 'Lead To Issue', type: 'ratio' },
  { key: 'demos', label: 'Demos', type: 'count' },
  { key: 'cpDemo', label: 'CP Demo', type: 'currency' },
  { key: 'demoRate', label: 'Demo Rate', type: 'ratio' },
  { key: 'grossSold', label: 'Gross Sold', type: 'count' },
  { key: 'netSold', label: 'Net Sold', type: 'count' },
  { key: 'leadToSale', label: 'Lead To Sale', type: 'ratio' },
  { key: 'netCloseRate', label: 'Net Close Rate', type: 'ratio' },
  { key: 'netAmount', label: 'Net Amount', type: 'currency' },
  { key: 'avgDeal', label: 'Avg Deal', type: 'currency' },
  { key: 'predictedCostOfMarketing', label: 'Pred. CoM', type: 'ratio' },
  { key: 'effectiveCostOfMarketing', label: 'Decision CoM', type: 'ratio' },
  { key: 'costOfMarketing', label: 'Cost of Marketing', type: 'ratio' },
  { key: 'creativeScore', label: 'Creative Score', type: 'count' },
  { key: 'businessScore', label: 'Business Score', type: 'count' },
  { key: 'overallScore', label: 'Overall Score', type: 'count' },
];

export const DECISION_KPI_OPTION = {
  key: 'effectiveCostOfMarketing',
  label: 'Pred./Actual CoM',
  better: 'lower',
  benchmarkScope: 'client_vertical',
  isEligible: (row) => row.hasBackendIntegration && safeNumber(row.effectiveCostOfMarketing) > 0,
};

export const KPI_STAGE_RULES = [
  {
    stageLabel: 'Sales Stage',
    thresholdLabel: '3 Net Sales',
    key: 'costOfMarketing',
    label: 'Cost of Marketing',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.netSold >= 3 && row.netAmount > 0,
  },
  {
    stageLabel: 'Demo Stage',
    thresholdLabel: '5 Demos',
    key: 'cpDemo',
    label: 'Cost Per Demo',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.demos >= 5,
  },
  {
    stageLabel: 'Issue Stage',
    thresholdLabel: '5 Issues',
    key: 'cpIssue',
    label: 'Cost Per Issue',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.issues >= 5,
  },
  {
    stageLabel: 'Appointment Stage',
    thresholdLabel: '8 Appointments',
    key: 'cpScheduled',
    label: 'Cost Per Appointment',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.scheduled >= 8,
  },
  {
    stageLabel: 'Self Book Stage',
    thresholdLabel: '10 Self Books',
    key: 'cpSelfBook',
    label: 'Cost Per Self Book',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.selfBook >= 10,
  },
  {
    stageLabel: 'Lead Stage',
    thresholdLabel: '20 Leads',
    key: 'cpl',
    label: 'CPL',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.leads >= 20,
  },
  {
    stageLabel: 'Creative Signal',
    thresholdLabel: '10K Impressions',
    key: 'ctr',
    label: 'CTR',
    better: 'higher',
    benchmarkScope: 'agency_vertical',
    qualifies: (row) => row.impressions >= 10000,
  },
];

export const WHY_COMPONENT_METRICS = [
  { key: 'ctr', label: 'CTR', better: 'higher', isEligible: (row) => row.impressions >= 1000 && safeNumber(row.ctr) > 0 },
  { key: 'lpvRate', label: 'LPV Rate', better: 'higher', isEligible: (row) => row.clicks >= 10 && safeNumber(row.lpvRate) > 0 },
  { key: 'hookRate', label: 'Hook Rate', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.videoPlays3s) > 0 && safeNumber(row.hookRate) > 0 },
  { key: 'thruplayRate', label: 'Thruplay Rate', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.thruplays) > 0 && safeNumber(row.thruplayRate) > 0 },
  { key: 'holdRate25', label: 'Hold 25%', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.videoP25) > 0 && safeNumber(row.holdRate25) > 0 },
  { key: 'holdRate50', label: 'Hold 50%', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.videoP50) > 0 && safeNumber(row.holdRate50) > 0 },
  { key: 'cpl', label: 'CPL', better: 'lower', isEligible: (row) => row.leads >= 3 && safeNumber(row.cpl) > 0 },
  { key: 'cpSelfBook', label: 'Cost per Self Book', better: 'lower', isEligible: (row) => row.selfBook >= 2 && safeNumber(row.cpSelfBook) > 0 },
  { key: 'setRate', label: 'Set Rate', better: 'higher', isEligible: (row) => row.leads >= 5 && safeNumber(row.setRate) > 0 },
  { key: 'cpScheduled', label: 'Cost per Appointment', better: 'lower', isEligible: (row) => row.scheduled >= 2 && safeNumber(row.cpScheduled) > 0 },
  { key: 'cpIssue', label: 'Cost per Issue', better: 'lower', isEligible: (row) => row.issues >= 2 && safeNumber(row.cpIssue) > 0 },
  { key: 'issueRate', label: 'Issue Rate', better: 'higher', isEligible: (row) => row.scheduled >= 2 && safeNumber(row.issueRate) > 0 },
  { key: 'cpDemo', label: 'Cost per Demo', better: 'lower', isEligible: (row) => row.demos >= 2 && safeNumber(row.cpDemo) > 0 },
  { key: 'demoRate', label: 'Demo Rate', better: 'higher', isEligible: (row) => row.issues >= 2 && safeNumber(row.demoRate) > 0 },
  { key: 'leadToSale', label: 'Lead to Sale', better: 'higher', isEligible: (row) => row.netSold >= 1 && safeNumber(row.leadToSale) > 0 },
  { key: 'netCloseRate', label: 'Net Close Rate', better: 'higher', isEligible: (row) => row.demos >= 2 && safeNumber(row.netCloseRate) > 0 },
];

// ─── Pure utility functions ─────────────────────────────────────────────────

export const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  if (!denom) return 0;
  return num / denom;
};

export const toOptionObjects = (values = []) =>
  values.map((value) => ({ label: value, value }));

export const arraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const clampScore = (value) => Math.max(0, Math.min(100, Math.round(value)));

export const scoreHigherIsBetter = (value, target, max = 1.25) =>
  clampScore((Math.min(safeDiv(value, target), max) / max) * 100);

export const scoreLowerIsBetter = (value, target, max = 2) => {
  if (!value) return 0;
  const inverted = safeDiv(target, value);
  return clampScore((Math.min(inverted, max) / max) * 100);
};

export const median = (values = []) => {
  const sorted = values
    .map((value) => safeNumber(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2) return sorted[middle];
  return (sorted[middle - 1] + sorted[middle]) / 2;
};

// ─── Format normalization ───────────────────────────────────────────────────

export const normalizeCampaignType = (value = '') => {
  const lowered = String(value).trim().toLowerCase();
  if (!lowered) return 'Unknown';
  if (lowered.includes('broad')) return 'Broad';
  if (lowered.includes('local') || lowered.includes('county')) return 'Local';
  return 'Unknown';
};

export const normalizeFacebookFormat = (value = '') => {
  const normalized = String(value || '').trim();
  const upper = normalized.toUpperCase();
  if (!upper) return '';
  if (VIDEO_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'VIDEO';
  if (DYNAMIC_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'DYNAMIC';
  if (CAROUSEL_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'CAROUSEL';
  if (STATIC_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'IMAGE';
  return normalized;
};

export const inferCreativeLabels = (row = {}) => {
  const explicitFormat = normalizeFacebookFormat(row.facebook_format || row.facebookFormat || '');
  const explicitAdType = String(row.ad_type || row.adType || '').trim();
  const normalizedAdType = normalizeFacebookFormat(explicitAdType);

  let facebookFormat = explicitFormat || normalizedAdType;

  if (!facebookFormat) {
    if (row.video_id || row.videoId) {
      facebookFormat = 'VIDEO';
    } else if (
      safeNumber(row.video_plays_3s_sum || row.videoPlays3s) > 0
      || safeNumber(row.thruplays_sum || row.thruplays) > 0
      || safeNumber(row.video_p25_sum || row.videoP25) > 0
    ) {
      facebookFormat = 'VIDEO';
    } else if (row.image_url || row.imageUrl || row.thumbnail_url || row.thumbnailUrl) {
      facebookFormat = 'IMAGE';
    }
  }

  let adType = explicitAdType;
  if (!adType) {
    if (facebookFormat === 'IMAGE') adType = 'Static';
    else if (facebookFormat === 'VIDEO') adType = 'Video';
    else if (facebookFormat === 'DYNAMIC') adType = 'Dynamic';
    else if (facebookFormat === 'CAROUSEL') adType = 'Carousel';
  }

  return {
    facebookFormat: facebookFormat || 'Unknown',
    adType: adType || 'Unknown',
  };
};

export const getResolvedCreativeFormat = (row = {}) =>
  normalizeFacebookFormat(
    row.facebookFormat
    || row.facebook_format
    || row.adType
    || row.ad_type
    || ''
  );

export const supportsVideoDiagnostics = (row = {}) =>
  getResolvedCreativeFormat(row) === 'VIDEO';

export const isVideoCreative = (row = {}) =>
  getResolvedCreativeFormat(row) === 'VIDEO';

// ─── Derived metrics ────────────────────────────────────────────────────────

export const computeDerivedMetrics = (base = {}) => {
  const spend = safeNumber(base.spend);
  const impressions = safeNumber(base.impressions);
  const clicks = safeNumber(base.clicks);
  const landingPageViews = safeNumber(base.landingPageViews);
  const videoPlays3s = safeNumber(base.videoPlays3s);
  const thruplays = safeNumber(base.thruplays);
  const videoP25 = safeNumber(base.videoP25);
  const videoP50 = safeNumber(base.videoP50);
  const videoP75 = safeNumber(base.videoP75);
  const videoP100 = safeNumber(base.videoP100);
  const leads = safeNumber(base.leads);
  const selfBook = safeNumber(base.selfBook);
  const matureSpend = base.matureSpend == null ? spend : safeNumber(base.matureSpend);
  const matureLeads = base.matureLeads == null ? leads : safeNumber(base.matureLeads);
  const booked = base.matureBooked == null ? safeNumber(base.booked) : safeNumber(base.matureBooked);
  const scheduled = base.matureScheduled == null ? safeNumber(base.scheduled) : safeNumber(base.matureScheduled);
  const issues = base.matureIssues == null ? safeNumber(base.issues) : safeNumber(base.matureIssues);
  const demos = base.matureDemos == null ? safeNumber(base.demos) : safeNumber(base.matureDemos);
  const grossSold = base.matureGrossSold == null ? safeNumber(base.grossSold) : safeNumber(base.matureGrossSold);
  const netSold = base.matureNetSold == null ? safeNumber(base.netSold) : safeNumber(base.matureNetSold);
  const netAmount = base.matureNetAmount == null ? safeNumber(base.netAmount) : safeNumber(base.matureNetAmount);

  return {
    spend,
    matureSpend,
    hookRate: safeDiv(videoPlays3s, impressions),
    ctr: safeDiv(clicks, impressions),
    lpvRate: safeDiv(landingPageViews, clicks),
    thruplayRate: safeDiv(thruplays, impressions),
    holdRate25: safeDiv(videoP25, videoPlays3s),
    holdRate50: safeDiv(videoP50, videoPlays3s),
    holdRate75: safeDiv(videoP75, videoPlays3s),
    holdRate100: safeDiv(videoP100, videoPlays3s),
    leads,
    cpl: safeDiv(spend, leads),
    selfBook,
    cpSelfBook: safeDiv(spend, selfBook),
    booked,
    cpBooked: safeDiv(matureSpend, booked),
    setRate: safeDiv(booked, matureLeads),
    scheduled,
    cpScheduled: safeDiv(matureSpend, scheduled),
    issues,
    cpIssue: safeDiv(matureSpend, issues),
    issueRate: safeDiv(issues, scheduled),
    leadToIssue: safeDiv(issues, matureLeads),
    demos,
    cpDemo: safeDiv(matureSpend, demos),
    demoRate: safeDiv(demos, issues),
    grossSold,
    netSold,
    leadToSale: safeDiv(netSold, matureLeads),
    netCloseRate: safeDiv(netSold, demos),
    netAmount,
    avgDeal: safeDiv(netAmount, netSold),
    costOfMarketing: safeDiv(matureSpend, netAmount),
  };
};

// ─── Date helpers ───────────────────────────────────────────────────────────

export const getAdAgeDays = (createdTime, referenceDate) => {
  if (!createdTime || !referenceDate) return null;
  const createdAt = new Date(createdTime);
  if (Number.isNaN(createdAt.getTime())) return null;
  const ref = new Date(referenceDate);
  ref.setHours(23, 59, 59, 999);
  const diffMs = ref.getTime() - createdAt.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

export const formatAdAge = (ageDays) => {
  if (ageDays == null) return 'Unknown';
  if (ageDays < 7) return `${ageDays}d`;
  if (ageDays < 30) return `${Math.floor(ageDays / 7)}w`;
  return `${Math.floor(ageDays / 30)}mo`;
};

export const formatShortDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatBackendFlag = (value) => (value ? 'Yes' : 'No');

export const getExpectedMatureEndDate = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return null;
  const matureEnd = new Date(endDate);
  matureEnd.setDate(matureEnd.getDate() - COHORT_MATURITY_LAG_DAYS);
  matureEnd.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  if (matureEnd < start) return null;
  return matureEnd;
};

export const getDateRangeDays = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diff = end.getTime() - start.getTime();
  return diff >= 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) + 1 : 0;
};

export const getLastThirtyDays = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return [start, end];
};

export const getRecentStartDate = (startDate, endDate) => {
  const recentStart = new Date(endDate);
  recentStart.setDate(recentStart.getDate() - (RECENT_WINDOW_DAYS - 1));
  return recentStart > startDate ? recentStart : startDate;
};

// ─── Lifecycle / stage ──────────────────────────────────────────────────────

export const getLifecycleLabel = ({ ageDays, spend, impressions, leads, issues, demos, netSold }) => {
  if (ageDays == null) return 'Unknown';
  if (ageDays <= 14 && spend < 100 && impressions < 5000) return 'New';
  if (spend > 100 || impressions > 5000 || leads > 0 || issues > 0 || demos > 0 || netSold > 0) {
    return 'Active';
  }
  if (ageDays > 30 && spend < 25 && impressions < 1000) return 'Deprioritized';
  if (ageDays > 14) return 'Dormant';
  return 'New';
};

export const getKpiStage = (row) => KPI_STAGE_RULES.find((rule) => rule.qualifies(row)) || null;

export const getMaturityLabel = (row) => {
  const stage = getKpiStage(row);
  if (stage) return stage.stageLabel;
  return 'Insufficient Data';
};

export const getPrimaryKpi = (row) => {
  const stage = getKpiStage(row);
  if (stage) {
    return {
      key: stage.key,
      label: stage.label,
      better: stage.better,
      benchmarkScope: stage.benchmarkScope,
      isEligible: (candidate) => {
        if (stage.key === 'costOfMarketing') return candidate.hasBackendIntegration && candidate.netSold >= 3 && candidate.netAmount > 0;
        if (stage.key === 'cpDemo') return candidate.hasBackendIntegration && candidate.demos >= 5;
        if (stage.key === 'cpIssue') return candidate.hasBackendIntegration && candidate.issues >= 5;
        if (stage.key === 'cpScheduled') return candidate.hasBackendIntegration && candidate.scheduled >= 8;
        if (stage.key === 'cpSelfBook') return candidate.selfBook >= 10;
        if (stage.key === 'cpl') return candidate.leads >= 20;
        if (stage.key === 'ctr') return candidate.impressions >= 10000;
        return false;
      },
    };
  }
  return null;
};

// ─── Benchmarks & predicted CoM ─────────────────────────────────────────────

export const getFunnelBenchmarks = (rows) => {
  const totals = rows.reduce(
    (acc, row) => {
      acc.leads += safeNumber(row.leads);
      acc.scheduled += safeNumber(row.scheduled);
      acc.issues += safeNumber(row.issues);
      acc.demos += safeNumber(row.demos);
      acc.netSold += safeNumber(row.netSold);
      acc.netAmount += safeNumber(row.netAmount);
      return acc;
    },
    { leads: 0, scheduled: 0, issues: 0, demos: 0, netSold: 0, netAmount: 0 }
  );

  return {
    leadToAppointment: safeDiv(totals.scheduled, totals.leads),
    appointmentToIssue: safeDiv(totals.issues, totals.scheduled),
    issueToDemo: safeDiv(totals.demos, totals.issues),
    demoToNetSale: safeDiv(totals.netSold, totals.demos),
    expectedAvgNetAmount: safeDiv(totals.netAmount, totals.netSold),
  };
};

export const hasUsableBenchmarks = (benchmarks) => (
  safeNumber(benchmarks.leadToAppointment) > 0
  && safeNumber(benchmarks.appointmentToIssue) > 0
  && safeNumber(benchmarks.issueToDemo) > 0
  && safeNumber(benchmarks.demoToNetSale) > 0
  && safeNumber(benchmarks.expectedAvgNetAmount) > 0
);

export const getPredictedCostPerNetSale = (row, benchmarks) => {
  if (!row?.hasBackendIntegration) return null;
  if (!hasUsableBenchmarks(benchmarks)) return null;

  if (row.demos >= 5 && benchmarks.demoToNetSale > 0) {
    return safeDiv(row.cpDemo, benchmarks.demoToNetSale);
  }
  if (row.issues >= 5) {
    return safeDiv(row.cpIssue, benchmarks.issueToDemo * benchmarks.demoToNetSale);
  }
  if (row.scheduled >= 8) {
    return safeDiv(
      row.cpScheduled,
      benchmarks.appointmentToIssue * benchmarks.issueToDemo * benchmarks.demoToNetSale
    );
  }
  if (row.selfBook >= 10 || row.leads >= 20) {
    return safeDiv(
      row.cpl,
      benchmarks.leadToAppointment
        * benchmarks.appointmentToIssue
        * benchmarks.issueToDemo
        * benchmarks.demoToNetSale
    );
  }
  return null;
};

export const getPredictedCostOfMarketing = (row, benchmarks) => {
  const predictedCostPerNetSale = getPredictedCostPerNetSale(row, benchmarks);
  if (!predictedCostPerNetSale || !benchmarks.expectedAvgNetAmount) return null;
  return safeDiv(predictedCostPerNetSale, benchmarks.expectedAvgNetAmount);
};

// ─── CoM confidence / deal stability ────────────────────────────────────────

export const getComConfidenceBand = (netSold) => {
  const sales = safeNumber(netSold);
  if (sales >= 30) return COM_CONFIDENCE_STABLE;
  if (sales >= 20) return COM_CONFIDENCE_DEVELOPING;
  if (sales >= 10) return COM_CONFIDENCE_EARLY;
  return COM_CONFIDENCE_NOT_ELIGIBLE;
};

export const getComBaseActualWeight = (band) => {
  if (band === COM_CONFIDENCE_STABLE) return 1;
  if (band === COM_CONFIDENCE_DEVELOPING) return 0.7;
  if (band === COM_CONFIDENCE_EARLY) return 0.35;
  return 0;
};

export const getDealStabilityLabel = (row = {}) =>
  row.dealValueStability || row.deal_value_stability || 'Unknown';

export const getDealStabilityDampener = (row = {}) => {
  const provided = safeNumber(row.dealValueStabilityDampener || row.deal_value_stability_dampener);
  if (provided > 0) return Math.min(1, provided);

  const stability = getDealStabilityLabel(row);
  if (stability === 'Stable') return 1;
  if (stability === 'Mixed') return 0.85;
  if (stability === 'Volatile') return 0.65;
  return 0.8;
};

export const getDecisionCostMetrics = (row, benchmarks) => {
  const predictedCostOfMarketing = getPredictedCostOfMarketing(row, benchmarks);
  const actualCostOfMarketing = row.netSold > 0 && row.costOfMarketing > 0
    ? row.costOfMarketing
    : null;
  const comConfidenceBand = getComConfidenceBand(row.netSold);
  const dealValueStability = getDealStabilityLabel(row);
  const dealValueStabilityDampener = getDealStabilityDampener(row);

  let actualWeight = getComBaseActualWeight(comConfidenceBand) * dealValueStabilityDampener;
  if (comConfidenceBand === COM_CONFIDENCE_STABLE && actualCostOfMarketing != null && dealValueStability === 'Stable') {
    actualWeight = 1;
  }
  actualWeight = Math.max(0, Math.min(1, actualWeight));

  let effectiveCostOfMarketing = null;
  if (actualCostOfMarketing != null && predictedCostOfMarketing != null) {
    effectiveCostOfMarketing = (predictedCostOfMarketing * (1 - actualWeight)) + (actualCostOfMarketing * actualWeight);
  } else if (actualCostOfMarketing != null) {
    effectiveCostOfMarketing = actualCostOfMarketing;
  } else {
    effectiveCostOfMarketing = predictedCostOfMarketing;
  }

  const decisionCostLabel = actualWeight >= 0.95
    ? 'Cost of Marketing'
    : actualWeight > 0
      ? 'Blended Cost of Marketing'
      : 'Predicted Cost of Marketing';

  return {
    predictedCostOfMarketing,
    actualCostOfMarketing,
    effectiveCostOfMarketing,
    comConfidenceBand,
    comConfidenceLabel: comConfidenceBand,
    dealValueStability,
    dealValueStabilityDampener,
    decisionCostActualWeight: actualWeight,
    decisionCostLabel,
  };
};

// ─── Benchmark delta (Fix 7: minimum peer count guard) ──────────────────────

export const getBenchmarkDelta = (row, peerRows, kpi) => {
  if (!kpi || !peerRows.length) return null;
  const eligiblePeers = peerRows.filter((peer) => kpi.isEligible(peer));
  if (eligiblePeers.length < MIN_PEER_COUNT) return null;
  const peerAverage = eligiblePeers.reduce((sum, peer) => sum + safeNumber(peer[kpi.key]), 0) / eligiblePeers.length;
  if (!peerAverage) return null;
  return safeDiv(safeNumber(row[kpi.key]) - peerAverage, peerAverage);
};

// ─── Vertical-aware scoring targets (Fix 8) ─────────────────────────────────

export const getVerticalBenchmarkTargets = (peerRows) => {
  const validValues = (key) => peerRows.map((p) => safeNumber(p[key])).filter((v) => v > 0);
  const m = (values) => values.length >= MIN_PEER_COUNT ? median(values) : null;

  return {
    creative: {
      ctr: m(validValues('ctr')) || DEFAULT_CREATIVE_TARGETS.ctr,
      lpvRate: m(validValues('lpvRate')) || DEFAULT_CREATIVE_TARGETS.lpvRate,
      hookRate: m(validValues('hookRate')) || DEFAULT_CREATIVE_TARGETS.hookRate,
      thruplayRate: m(validValues('thruplayRate')) || DEFAULT_CREATIVE_TARGETS.thruplayRate,
      holdRate50: m(validValues('holdRate50')) || DEFAULT_CREATIVE_TARGETS.holdRate50,
    },
    business: {
      cpl: m(validValues('cpl')) || DEFAULT_BUSINESS_TARGETS.cpl,
      cpSelfBook: m(validValues('cpSelfBook')) || DEFAULT_BUSINESS_TARGETS.cpSelfBook,
      cpScheduled: m(validValues('cpScheduled')) || DEFAULT_BUSINESS_TARGETS.cpScheduled,
      cpIssue: m(validValues('cpIssue')) || DEFAULT_BUSINESS_TARGETS.cpIssue,
      cpDemo: m(validValues('cpDemo')) || DEFAULT_BUSINESS_TARGETS.cpDemo,
      costOfMarketing: m(validValues('effectiveCostOfMarketing')) || m(validValues('costOfMarketing')) || DEFAULT_BUSINESS_TARGETS.costOfMarketing,
    },
  };
};

// ─── Scoring (Fix 8: vertical-aware targets) ────────────────────────────────

export const getCreativeScore = (row, targets = DEFAULT_CREATIVE_TARGETS) => {
  const parts = [
    scoreHigherIsBetter(row.ctr, targets.ctr),
    scoreHigherIsBetter(row.lpvRate, targets.lpvRate),
  ];

  if (isVideoCreative(row)) {
    parts.push(
      scoreHigherIsBetter(row.hookRate, targets.hookRate),
      scoreHigherIsBetter(row.thruplayRate, targets.thruplayRate),
      scoreHigherIsBetter(row.holdRate50, targets.holdRate50)
    );
  }

  return clampScore(parts.reduce((sum, value) => sum + value, 0) / parts.length);
};

export const getBusinessScore = (row, targets = DEFAULT_BUSINESS_TARGETS) => {
  const weighted = [
    { score: scoreLowerIsBetter(row.cpl, targets.cpl), weight: 1 },
    { score: scoreLowerIsBetter(row.cpSelfBook, targets.cpSelfBook), weight: 1 },
    { score: scoreLowerIsBetter(row.cpScheduled, targets.cpScheduled), weight: 1 },
    { score: scoreLowerIsBetter(row.cpIssue, targets.cpIssue), weight: 1.25 },
    { score: scoreLowerIsBetter(row.cpDemo, targets.cpDemo), weight: 1.5 },
    { score: scoreLowerIsBetter(row.effectiveCostOfMarketing || row.costOfMarketing, targets.costOfMarketing), weight: 2.5 },
  ];
  const totalWeight = weighted.reduce((sum, w) => sum + w.weight, 0);
  const totalScore = weighted.reduce((sum, w) => sum + w.score * w.weight, 0);
  return clampScore(totalScore / totalWeight);
};

export const getStageScoreWeights = (maturityLabel) => {
  if (maturityLabel === 'Insufficient Data') return { creativeWeight: 1, businessWeight: 0 };
  if (maturityLabel === 'Creative Signal') return { creativeWeight: 0.9, businessWeight: 0.1 };
  if (maturityLabel === 'Lead Stage') return { creativeWeight: 0.7, businessWeight: 0.3 };
  if (maturityLabel === 'Self Book Stage') return { creativeWeight: 0.6, businessWeight: 0.4 };
  if (maturityLabel === 'Appointment Stage') return { creativeWeight: 0.5, businessWeight: 0.5 };
  if (maturityLabel === 'Issue Stage') return { creativeWeight: 0.45, businessWeight: 0.55 };
  if (maturityLabel === 'Demo Stage') return { creativeWeight: 0.35, businessWeight: 0.65 };
  if (maturityLabel === 'Sales Stage') return { creativeWeight: 0.25, businessWeight: 0.75 };
  return { creativeWeight: 0.5, businessWeight: 0.5 };
};

export const getOverallScore = (row, creativeScore, businessScore, confidenceScore = 100) => {
  const { creativeWeight, businessWeight } = getStageScoreWeights(row.maturityLabel);
  if (!businessWeight) return clampScore(creativeScore);

  const normalizedConfidence = Math.max(0, Math.min(1, safeNumber(confidenceScore) / 100));
  const businessTrust = 0.35 + (normalizedConfidence * 0.65);
  const effectiveBusinessWeight = businessWeight * businessTrust;
  const effectiveCreativeWeight = 1 - effectiveBusinessWeight;

  return clampScore((creativeScore * effectiveCreativeWeight) + (businessScore * effectiveBusinessWeight));
};

export const getBenchmarkAdjustedScore = (baseScore, delta, better = 'higher') => {
  if (delta == null) return clampScore(baseScore);
  const normalizedDelta = better === 'lower' ? -safeNumber(delta) : safeNumber(delta);
  const adjustment = Math.max(-20, Math.min(20, normalizedDelta * 40));
  return clampScore(baseScore + adjustment);
};

// ─── Confidence ─────────────────────────────────────────────────────────────

export const getConfidenceScore = (row) => {
  let score = 0;
  if (row.impressions >= 25000) score += 20;
  else if (row.impressions >= 10000) score += 16;
  else if (row.impressions >= 5000) score += 12;
  else if (row.impressions >= 1000) score += 8;
  else if (row.impressions > 0) score += 4;

  if (row.leads >= 25) score += 20;
  else if (row.leads >= 15) score += 16;
  else if (row.leads >= 10) score += 12;
  else if (row.leads >= 5) score += 8;
  else if (row.leads > 0) score += 4;

  if (row.issues >= 25) score += 20;
  else if (row.issues >= 8) score += 16;
  else if (row.issues >= 5) score += 12;
  else if (row.issues >= 2) score += 8;
  else if (row.issues > 0) score += 4;

  if (row.demos >= 25) score += 20;
  else if (row.demos >= 4) score += 16;
  else if (row.demos >= 2) score += 12;
  else if (row.demos > 0) score += 6;

  if (row.netSold >= 10) score += 20;
  else if (row.netSold >= 2) score += 16;
  else if (row.netSold >= 1) score += 10;
  else if (row.netAmount >= 5000) score += 8;

  return clampScore(score);
};

export const getConfidenceLabel = (score) => {
  if (score >= 75) return 'High';
  if (score >= 45) return 'Medium';
  return 'Low';
};

// ─── Formatting ─────────────────────────────────────────────────────────────

export const formatDeltaLabel = (delta) => {
  if (delta == null) return '-';
  const percentage = `${Math.abs(delta * 100).toFixed(0)}%`;
  return delta >= 0 ? `+${percentage}` : `-${percentage}`;
};

export const formatPercentLabel = (value) => {
  if (value == null) return '-';
  return `${Math.round(safeNumber(value) * 100)}%`;
};

export const formatPriorityLabel = (value) => `${clampScore(value)}/100`;

export const getDirectionalDelta = (delta, better = 'higher') => {
  if (delta == null) return 0;
  return better === 'lower' ? -safeNumber(delta) : safeNumber(delta);
};

// ─── Trend / recommendation ─────────────────────────────────────────────────

export const getTrendLabel = (overallDelta, recentDelta, better = 'higher') => {
  if (overallDelta == null || recentDelta == null) return 'Flat';
  const directionalRecent = better === 'lower' ? -safeNumber(recentDelta) : safeNumber(recentDelta);
  const directionalOverall = better === 'lower' ? -safeNumber(overallDelta) : safeNumber(overallDelta);
  const change = directionalRecent - directionalOverall;
  if (change >= 0.1) return 'Improving';
  if (change <= -0.1) return 'Declining';
  return 'Flat';
};

export const getStrongestWhyDriver = (whyDrivers = [], direction = 'positive') => {
  if (direction === 'negative') {
    return whyDrivers.find((driver) => driver.directionalDelta <= -0.12) || null;
  }
  return whyDrivers.find((driver) => driver.directionalDelta >= 0.12) || null;
};

export const gateRecommendationByComConfidence = ({
  recommendation,
  primaryKpi,
  comConfidenceBand,
  dealValueStability,
  confidenceScore,
  whyDrivers,
}) => {
  if (primaryKpi?.key !== 'effectiveCostOfMarketing') return recommendation;
  if (!['Keep Running', 'Pause'].includes(recommendation)) return recommendation;

  const fallback = confidenceScore < 45 ? 'Test Longer' : 'Watch';
  const positiveSupport = Boolean(getStrongestWhyDriver(whyDrivers, 'positive'));
  const negativeSupport = Boolean(getStrongestWhyDriver(whyDrivers, 'negative'));
  const alignedSupport = recommendation === 'Keep Running' ? positiveSupport : negativeSupport;
  const volatileDeals = dealValueStability === 'Volatile';

  if (comConfidenceBand === COM_CONFIDENCE_EARLY) {
    return alignedSupport && confidenceScore >= 55 && !volatileDeals ? recommendation : fallback;
  }

  if (comConfidenceBand === COM_CONFIDENCE_DEVELOPING) {
    if (volatileDeals && !alignedSupport) return 'Watch';
    return confidenceScore >= 45 ? recommendation : fallback;
  }

  if (comConfidenceBand === COM_CONFIDENCE_STABLE) {
    if (volatileDeals && confidenceScore < 60 && !alignedSupport) return 'Watch';
    return recommendation;
  }

  return fallback;
};

export const getRecommendation = (row, primaryKpi, benchmarkDelta) => {
  if (!primaryKpi) return 'Test Longer';
  if (row.lifecycleLabel === 'Deprioritized') return 'Dormant';
  if (row.maturityLabel === 'Insufficient Data') return 'Test Longer';
  if (safeNumber(row.impressions) < MIN_IMPRESSIONS_FOR_ACTION) return 'Test Longer';
  if (benchmarkDelta == null) return 'Watch';

  if (primaryKpi.better === 'lower') {
    if (benchmarkDelta <= -0.15) return 'Keep Running';
    if (benchmarkDelta >= 0.2) return 'Pause';
  } else {
    if (benchmarkDelta >= 0.15) return 'Keep Running';
    if (benchmarkDelta <= -0.2) return 'Pause';
  }

  if (Math.abs(benchmarkDelta) <= 0.1) return 'Keep Running';
  return 'Watch';
};

// ─── Why drivers ────────────────────────────────────────────────────────────

export const getWhyDrivers = (row, peerRows) => {
  if (!peerRows.length) return [];

  return WHY_COMPONENT_METRICS
    .filter((metric) => metric.isEligible(row))
    .map((metric) => {
      const delta = getBenchmarkDelta(row, peerRows, {
        key: metric.key,
        better: metric.better,
        isEligible: metric.isEligible,
      });
      const directionalDelta = getDirectionalDelta(delta, metric.better);
      return { ...metric, delta, directionalDelta };
    })
    .filter((metric) => metric.delta != null)
    .sort((left, right) => Math.abs(right.directionalDelta) - Math.abs(left.directionalDelta));
};

export const getTopWhyDriverChips = (whyDrivers = [], limit = 2) =>
  whyDrivers
    .filter((driver) => Math.abs(safeNumber(driver.directionalDelta)) >= 0.05)
    .slice(0, limit);

export const formatWhyDriverChip = (driver) => {
  if (!driver || driver.delta == null) return null;
  const sign = driver.directionalDelta >= 0 ? '+' : '-';
  const amount = Math.abs(safeNumber(driver.delta) * 100).toFixed(0);
  return `${driver.label} ${sign}${amount}%`;
};

export const formatWhyDriverComparison = (driver) => {
  if (!driver || driver.delta == null) return '';
  const amount = `${Math.abs(safeNumber(driver.delta) * 100).toFixed(0)}%`;
  return driver.directionalDelta >= 0 ? `${amount} better than peers` : `${amount} worse than peers`;
};

export const getWhyLabel = ({
  row,
  primaryKpi,
  benchmarkDelta,
  benchmarkScope,
  recommendation,
  diagnosticLabel,
  maturityLabel,
  spendShare,
  confidenceLabel,
  whyDrivers,
}) => {
  if (!primaryKpi) return 'Not enough mature data yet.';
  if (recommendation === 'Test Longer') return 'Not enough mature data yet.';
  if (recommendation === 'Dormant') return 'Older ad with very low recent delivery.';
  if (!row?.hasBackendIntegration && ['Lead Stage', 'Self Book Stage'].includes(maturityLabel)) {
    return 'This client has no backend integration, so evaluation stops at lead and self-book stages.';
  }
  if (benchmarkDelta == null) return 'No valid peer benchmark in the current scope.';

  const scopeLabel = benchmarkScope === 'agency_vertical' ? 'agency vertical' : 'client vertical';
  const deltaText = `${Math.abs(safeNumber(benchmarkDelta) * 100).toFixed(0)}%`;
  const betterIsHigher = primaryKpi.better === 'higher';
  const isWinning = betterIsHigher ? benchmarkDelta >= 0 : benchmarkDelta <= 0;
  const relativeText = isWinning ? 'above' : 'below';
  const strongestDriver = whyDrivers.find((driver) => driver.directionalDelta > 0.05) || null;
  const weakestDriver = whyDrivers.find((driver) => driver.directionalDelta < -0.05) || null;

  if (strongestDriver || weakestDriver) {
    if (isWinning) {
      if (strongestDriver && weakestDriver) {
        return `${strongestDriver.label} is leading (${formatWhyDriverComparison(strongestDriver)}); biggest drag is ${weakestDriver.label.toLowerCase()} (${formatWhyDriverComparison(weakestDriver)}).`;
      }
      if (strongestDriver) {
        return `${strongestDriver.label} is the main advantage at ${formatWhyDriverComparison(strongestDriver)}.`;
      }
    } else {
      if (weakestDriver && strongestDriver) {
        return `${weakestDriver.label} is the main drag (${formatWhyDriverComparison(weakestDriver)}); best offset is ${strongestDriver.label.toLowerCase()} (${formatWhyDriverComparison(strongestDriver)}).`;
      }
      if (weakestDriver) {
        return `${weakestDriver.label} is the main drag at ${formatWhyDriverComparison(weakestDriver)}.`;
      }
    }
  }

  if (maturityLabel === 'Creative Signal' && primaryKpi.key === 'ctr') {
    if (isVideoCreative(row)) {
      return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on CTR by ${deltaText}; Hook Rate is the secondary video diagnostic.`;
    }
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on CTR by ${deltaText}.`;
  }
  if (primaryKpi.key === 'effectiveCostOfMarketing') {
    const spendText = safeNumber(spendShare) >= 0.1 ? ` with ${formatPercentLabel(spendShare)} of spend` : '';
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on projected/actual Cost of Marketing by ${deltaText}${spendText}.`;
  }
  if (diagnosticLabel === 'Funnel Problem') {
    return `Creative is engaging, but backend quality is ${relativeText === 'above' ? 'still mixed' : 'lagging'} peers.`;
  }
  if (diagnosticLabel === 'Creative Problem') {
    return `Backend outcomes are acceptable, but top-funnel creative engagement trails peers.`;
  }
  if (diagnosticLabel === 'Backend Problem') {
    return `Ads are reaching demos, but sales conversion is still weak.`;
  }
  if (recommendation === 'Pause' && confidenceLabel === 'High') {
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on ${primaryKpi.label} by ${deltaText} with high confidence.`;
  }
  return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on ${primaryKpi.label} by ${deltaText}.`;
};

// ─── Verdicts / diagnostics ─────────────────────────────────────────────────

export const getCreativeVerdict = (row, creativeScore, overallScore) => {
  if (row.maturityLabel === 'Insufficient Data') return 'Needs More Data';
  if (overallScore >= 75) return 'Winning';
  if (creativeScore >= 65 || overallScore >= 60) return 'Promising';
  if (overallScore <= 40) return 'Weak';
  return 'Mixed';
};

export const getDiagnosticLabel = (row, creativeScore, businessScore) => {
  if (row.maturityLabel === 'Insufficient Data') return 'Needs More Data';
  if (creativeScore >= 65 && businessScore < 45) return 'Funnel Problem';
  if (creativeScore < 45 && businessScore >= 55) return 'Creative Problem';
  if (row.netSold === 0 && row.demos > 0) return 'Backend Problem';
  if (creativeScore >= 65 && businessScore >= 65) return 'Balanced Winner';
  return 'Mixed Signals';
};

// ─── Priority scores ────────────────────────────────────────────────────────

export const getPausePriority = ({
  overallRecommendation,
  recentRecommendation,
  creativeVerdict,
  diagnosticLabel,
  confidenceScore,
  spendShare,
  directionalDelta,
}) => {
  let score = 0;
  if (overallRecommendation === 'Pause') score += 42;
  else if (overallRecommendation === 'Watch') score += 12;
  else if (overallRecommendation === 'Dormant') score += 8;

  if (recentRecommendation === 'Pause') score += 12;
  else if (recentRecommendation === 'Watch') score += 4;

  if (directionalDelta < 0) {
    score += Math.min(24, Math.abs(directionalDelta) * 55);
  }

  if (creativeVerdict === 'Weak') score += 10;
  if (diagnosticLabel === 'Funnel Problem' || diagnosticLabel === 'Backend Problem') score += 6;

  score += safeNumber(confidenceScore) * 0.18;
  score += Math.min(18, safeNumber(spendShare) * 120);

  if (overallRecommendation === 'Test Longer') score *= 0.45;
  if (overallRecommendation === 'Keep Running') score *= 0.15;

  return clampScore(score);
};

export const getBuildPriority = ({
  overallRecommendation,
  recentRecommendation,
  creativeVerdict,
  confidenceScore,
  spendShare,
  directionalDelta,
  stageLabel,
}) => {
  let score = 0;
  if (overallRecommendation === 'Keep Running') score += 42;
  else if (overallRecommendation === 'Watch') score += 6;

  if (recentRecommendation === 'Keep Running') score += 12;
  else if (recentRecommendation === 'Watch') score += 4;

  if (directionalDelta > 0) {
    score += Math.min(24, directionalDelta * 55);
  }

  if (creativeVerdict === 'Winning') score += 12;
  else if (creativeVerdict === 'Promising') score += 8;

  score += safeNumber(confidenceScore) * 0.18;
  score += Math.min(12, safeNumber(spendShare) * 80);

  if (stageLabel === 'Creative Signal') score *= 0.8;
  if (overallRecommendation === 'Pause' || overallRecommendation === 'Dormant') score *= 0.1;

  return clampScore(score);
};

// ─── Peer rank / stability ──────────────────────────────────────────────────

export const getPeerRankLabel = (row, peerRows, kpi) => {
  if (!kpi || !peerRows.length) return '-';
  const eligiblePeers = peerRows.filter((peer) => kpi.isEligible(peer));
  if (!eligiblePeers.length) return '-';

  const comparator = kpi.better === 'lower'
    ? (peer) => safeNumber(peer[kpi.key]) >= safeNumber(row[kpi.key])
    : (peer) => safeNumber(peer[kpi.key]) <= safeNumber(row[kpi.key]);
  const beatingCount = eligiblePeers.filter(comparator).length;
  const percentile = Math.round((beatingCount / eligiblePeers.length) * 100);
  if (percentile >= 90) return 'Top 10%';
  if (percentile >= 75) return 'Top Quartile';
  if (percentile <= 25) return 'Bottom Quartile';
  return 'Middle Pack';
};

export const getStabilityLabel = (recentRecommendation, overallRecommendation, trendLabel) => {
  if (recentRecommendation === 'Keep Running' && overallRecommendation === 'Keep Running') return 'Consistent Winner';
  if (recentRecommendation === 'Pause' && overallRecommendation === 'Pause') return 'Consistent Weak';
  if (recentRecommendation === 'Keep Running' && overallRecommendation !== 'Keep Running') return 'Emerging';
  if (recentRecommendation === 'Pause' && overallRecommendation !== 'Pause') return 'Fading';
  if (trendLabel === 'Improving') return 'Building';
  if (trendLabel === 'Declining') return 'Slipping';
  return 'Mixed';
};

// ─── Pattern leaders ────────────────────────────────────────────────────────

export const aggregatePatternLeaders = (rows, key) => {
  const grouped = new Map();
  rows.forEach((row) => {
    const groupKey = row[key];
    if (!groupKey || groupKey === '-') return;
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        name: groupKey,
        count: 0,
        spend: 0,
        creativeScore: 0,
        businessScore: 0,
        overallScore: 0,
        confidenceScore: 0,
        effectiveCostOfMarketing: 0,
        effectiveCostOfMarketingCount: 0,
        keepRunningCount: 0,
        watchCount: 0,
        testLongerCount: 0,
        pauseCount: 0,
      });
    }
    const bucket = grouped.get(groupKey);
    bucket.count += 1;
    bucket.spend += safeNumber(row.spend);
    bucket.creativeScore += safeNumber(row.creativeScore);
    bucket.businessScore += safeNumber(row.businessScore);
    bucket.overallScore += safeNumber(row.overallScore);
    bucket.confidenceScore += safeNumber(row.confidenceScore);
    if (safeNumber(row.effectiveCostOfMarketing) > 0) {
      bucket.effectiveCostOfMarketing += safeNumber(row.effectiveCostOfMarketing);
      bucket.effectiveCostOfMarketingCount += 1;
    }
    if (row.overallRecommendation === 'Keep Running') bucket.keepRunningCount += 1;
    if (row.overallRecommendation === 'Watch') bucket.watchCount += 1;
    if (row.overallRecommendation === 'Test Longer') bucket.testLongerCount += 1;
    if (row.overallRecommendation === 'Pause') bucket.pauseCount += 1;
  });

  return Array.from(grouped.values()).map((bucket) => ({
    ...bucket,
    creativeScore: clampScore(bucket.creativeScore / bucket.count),
    businessScore: clampScore(bucket.businessScore / bucket.count),
    overallScore: clampScore(bucket.overallScore / bucket.count),
    confidenceScore: clampScore(bucket.confidenceScore / bucket.count),
  })).map((bucket) => {
    const confidenceFactor = 0.45 + (safeNumber(bucket.confidenceScore) / 100) * 0.55;
    const adCountFactor = Math.min(1, safeDiv(bucket.count, 3));
    const spendFactor = Math.min(1, safeDiv(bucket.spend, 10000));
    const volumeFactor = 0.5 + ((adCountFactor * 0.6) + (spendFactor * 0.4)) * 0.5;

    return {
      ...bucket,
      confidenceLabel: getConfidenceLabel(bucket.confidenceScore),
      rankingScore: clampScore(bucket.overallScore * confidenceFactor * volumeFactor),
      effectiveCostOfMarketingAvg: bucket.effectiveCostOfMarketingCount
        ? safeDiv(bucket.effectiveCostOfMarketing, bucket.effectiveCostOfMarketingCount)
        : null,
    };
  });
};

export const getBestComponentLeaders = (rows, key, limit = 5) =>
  aggregatePatternLeaders(rows, key)
    .sort((a, b) => b.rankingScore - a.rankingScore)
    .slice(0, limit);

export const getPatternRecommendationSummary = (pattern = {}) => {
  const parts = [];
  if (pattern.keepRunningCount) parts.push(`${pattern.keepRunningCount} Keep`);
  if (pattern.watchCount) parts.push(`${pattern.watchCount} Watch`);
  if (pattern.testLongerCount) parts.push(`${pattern.testLongerCount} Test`);
  if (pattern.pauseCount) parts.push(`${pattern.pauseCount} Pause`);
  return parts.join(' \u2022 ') || 'No action mix yet';
};

export const getRankedRows = (rows, kpiOption, direction) => {
  if (!kpiOption) return [];

  const eligibleRows = rows
    .filter((row) => row.spend > 0)
    .filter((row) => kpiOption.isEligible(row));

  const sorted = [...eligibleRows].sort((left, right) => {
    const leftValue = safeNumber(left[kpiOption.key]);
    const rightValue = safeNumber(right[kpiOption.key]);

    if (kpiOption.better === 'lower') {
      return direction === 'best' ? leftValue - rightValue : rightValue - leftValue;
    }
    return direction === 'best' ? rightValue - leftValue : leftValue - rightValue;
  });

  return sorted.slice(0, 5);
};

// ─── Hierarchy builder ──────────────────────────────────────────────────────

export const buildAdHierarchy = (rows = []) => {
  const clientMap = new Map();

  rows.forEach((row) => {
    const clientKey = `${row.clientId || 'unknown-client'}::${row.clientName || 'Unknown Client'}`;
    if (!clientMap.has(clientKey)) {
      clientMap.set(clientKey, {
        key: clientKey,
        clientName: row.clientName || 'Unknown Client',
        verticalName: row.verticalName || '',
        spend: 0,
        rows: [],
        campaigns: new Map(),
      });
    }

    const clientGroup = clientMap.get(clientKey);
    clientGroup.spend += safeNumber(row.spend);
    clientGroup.rows.push(row);

    const campaignLabel = row.campaign || 'Unknown Campaign';
    const campaignKey = `${clientKey}::${campaignLabel}`;
    if (!clientGroup.campaigns.has(campaignKey)) {
      clientGroup.campaigns.set(campaignKey, {
        key: campaignKey,
        campaign: campaignLabel,
        spend: 0,
        rows: [],
        adSets: new Map(),
      });
    }

    const campaignGroup = clientGroup.campaigns.get(campaignKey);
    campaignGroup.spend += safeNumber(row.spend);
    campaignGroup.rows.push(row);

    const adSetLabel = row.adSetName || 'Unknown Ad Set';
    const adSetKey = `${campaignKey}::${adSetLabel}`;
    if (!campaignGroup.adSets.has(adSetKey)) {
      campaignGroup.adSets.set(adSetKey, {
        key: adSetKey,
        adSetName: adSetLabel,
        spend: 0,
        rows: [],
      });
    }

    const adSetGroup = campaignGroup.adSets.get(adSetKey);
    adSetGroup.spend += safeNumber(row.spend);
    adSetGroup.rows.push(row);
  });

  return Array.from(clientMap.values())
    .map((clientGroup) => ({
      ...clientGroup,
      campaigns: Array.from(clientGroup.campaigns.values())
        .map((campaignGroup) => ({
          ...campaignGroup,
          adSets: Array.from(campaignGroup.adSets.values())
            .map((adSetGroup) => ({
              ...adSetGroup,
              rows: [...adSetGroup.rows].sort((left, right) => right.spend - left.spend),
            }))
            .sort((left, right) => right.spend - left.spend),
        }))
        .sort((left, right) => right.spend - left.spend),
    }))
    .sort((left, right) => right.spend - left.spend);
};

// ─── Range readiness ────────────────────────────────────────────────────────

export const getSpendConcentrationStats = (rows = []) => {
  const spends = rows
    .map((row) => safeNumber(row.spend))
    .filter((value) => value > 0)
    .sort((a, b) => b - a);
  const totalSpend = spends.reduce((sum, value) => sum + value, 0);
  if (!totalSpend) {
    return { top1Share: 0, top3Share: 0, fragmented: false };
  }
  const top1Share = safeDiv(spends[0], totalSpend);
  const top3Share = safeDiv(spends.slice(0, 3).reduce((sum, value) => sum + value, 0), totalSpend);
  return {
    top1Share,
    top3Share,
    fragmented: top1Share < 0.2 && top3Share < 0.55,
  };
};

export const getBackendLagModifier = (rows = []) => {
  const backendRows = rows.filter((row) => row.hasBackendIntegration);
  if (!backendRows.length) {
    return { lagScore: 0, lagged: false, reason: 'No backend lag modifier applied.' };
  }

  const medianLeads = median(backendRows.map((row) => row.leads));
  const medianIssues = median(backendRows.map((row) => row.issues));
  const medianDemos = median(backendRows.map((row) => row.demos));
  const medianNetSales = median(backendRows.map((row) => row.netSold));

  let lagScore = 0;
  if (medianLeads >= 8 && medianIssues < 2) lagScore += 1;
  if (medianIssues >= 4 && medianDemos < 1) lagScore += 1;
  if (medianDemos >= 2 && medianNetSales < 1) lagScore += 1;

  if (lagScore >= 2) {
    return {
      lagScore,
      lagged: true,
      reason: 'Backend outcomes appear to lag top-funnel volume, so a longer decision window is safer.',
    };
  }

  return {
    lagScore,
    lagged: false,
    reason: 'Backend progression does not currently suggest a longer lag-adjusted window.',
  };
};

export const getRangeReadiness = (rows = []) => {
  const activeRows = rows.filter((row) => row.isActive && safeNumber(row.spend) > 0);
  const activeAdCount = activeRows.length;
  const medianLeads = median(activeRows.map((row) => row.leads));
  const medianIssues = median(activeRows.map((row) => row.issues));
  const medianDemos = median(activeRows.map((row) => row.demos));
  const medianNetSales = median(activeRows.map((row) => row.netSold));
  const spendStats = getSpendConcentrationStats(activeRows);
  const lagModifier = getBackendLagModifier(activeRows);

  const enoughUsefulVolume = (
    medianLeads >= 5
    || medianIssues >= 2
    || medianDemos >= 1
    || medianNetSales >= 1
  );

  return {
    activeAdCount,
    medianLeads,
    medianIssues,
    medianDemos,
    medianNetSales,
    enoughUsefulVolume,
    spendConcentration: spendStats,
    lagModifier,
  };
};

export const getRecommendedLookbackGuidance = ({
  rows = [],
  recentRows = [],
  currentRangeDays = 0,
}) => {
  const activeRows = rows.filter((row) => row.isActive && safeNumber(row.spend) > 0);
  const recentActiveRows = recentRows.filter((row) => row.isActive && safeNumber(row.spend) > 0);
  const recentReadiness = getRangeReadiness(recentActiveRows);
  const currentReadiness = getRangeReadiness(activeRows);

  let recommendedDays = 14;
  let reason = 'Median active ads already have enough recent volume for a shorter decision window.';

  if (!recentReadiness.enoughUsefulVolume) {
    recommendedDays = 30;
    reason = 'The recent 14-day window is thin at the ad level, so a longer window is recommended.';
  }

  const fragmented = (
    currentReadiness.activeAdCount >= 8
    && currentReadiness.spendConcentration.fragmented
  );
  const stillThinAtCurrent = currentRangeDays >= 30 && !currentReadiness.enoughUsefulVolume;

  if (
    recommendedDays === 30
    && (stillThinAtCurrent || fragmented || currentReadiness.lagModifier.lagged)
  ) {
    recommendedDays = 45;
    reason = fragmented
      ? 'Spend is split across many active ads, so each ad needs a longer window to mature.'
      : currentReadiness.lagModifier.lagged
        ? currentReadiness.lagModifier.reason
        : 'A 30-day window is still thin at the ad level, so 45 days is recommended.';
  }

  const detailBits = [
    `${recentReadiness.activeAdCount || currentReadiness.activeAdCount} active ads`,
    `median leads ${recentReadiness.medianLeads.toFixed(1)}`,
    `median issues ${recentReadiness.medianIssues.toFixed(1)}`,
    `median demos ${recentReadiness.medianDemos.toFixed(1)}`,
    `median net sales ${recentReadiness.medianNetSales.toFixed(1)}`,
    `top ad spend share ${Math.round((currentReadiness.spendConcentration.top1Share || recentReadiness.spendConcentration.top1Share) * 100)}%`,
  ];

  return {
    recommendedDays,
    label: `Recommended: Last ${recommendedDays} days`,
    reason,
    detailText: detailBits.join(' \u2022 '),
  };
};

// ─── Pause audit ────────────────────────────────────────────────────────────

export const buildPauseAuditNote = (row = {}) => {
  const parts = [];
  if (row.overallRecommendation) parts.push(`Decision: ${row.overallRecommendation}.`);
  if (row.whyLabel) parts.push(row.whyLabel);
  const chips = Array.isArray(row.whyDriverChips)
    ? row.whyDriverChips.map((driver) => formatWhyDriverChip(driver)).filter(Boolean)
    : [];
  if (chips.length) parts.push(`Drivers: ${chips.join(', ')}.`);
  if (row.comConfidenceLabel) parts.push(`CoM Confidence: ${row.comConfidenceLabel}.`);
  if (row.dealValueStability) parts.push(`Deal Stability: ${row.dealValueStability}.`);
  return parts.join(' ').trim();
};

// ─── Tone helpers ───────────────────────────────────────────────────────────

export const getStatusPillStyles = (tone) => {
  const palette = {
    success: { bg: '#dcfce7', fg: '#166534' },
    warning: { bg: '#fef3c7', fg: '#92400e' },
    danger: { bg: '#fee2e2', fg: '#b91c1c' },
    info: { bg: '#dbeafe', fg: '#1d4ed8' },
    neutral: { bg: '#e5e7eb', fg: '#374151' },
    violet: { bg: '#ede9fe', fg: '#6d28d9' },
  };
  return palette[tone] || palette.neutral;
};

export const getRatioTone = (value) => {
  const numeric = safeNumber(value);
  if (!numeric) return 'neutral';
  if (numeric <= 0.2) return 'success';
  if (numeric <= 0.35) return 'warning';
  return 'danger';
};

export const getPriorityTone = (value) => {
  const numeric = safeNumber(value);
  if (numeric >= 70) return 'danger';
  if (numeric >= 40) return 'warning';
  return 'neutral';
};

export const getBuildTone = (value) => {
  const numeric = safeNumber(value);
  if (numeric >= 70) return 'success';
  if (numeric >= 40) return 'info';
  return 'neutral';
};

export const getConfidenceTone = (label) => {
  if (label === 'High') return 'success';
  if (label === 'Medium') return 'warning';
  return 'neutral';
};

export const getRecommendationTone = (label) => {
  if (label === 'Pause') return 'danger';
  if (label === 'Keep Running') return 'success';
  if (label === 'Test Longer') return 'warning';
  if (label === 'Watch') return 'info';
  return 'neutral';
};

export const getActiveTone = (isActive) => (isActive ? 'success' : 'danger');

export const getVerdictTone = (label) => {
  if (label === 'Winning') return 'success';
  if (label === 'Promising') return 'info';
  if (label === 'Weak') return 'danger';
  if (label === 'Mixed') return 'warning';
  return 'neutral';
};

export const getStageTone = (label) => {
  if (label === 'Sales Stage') return 'success';
  if (label === 'Demo Stage' || label === 'Issue Stage' || label === 'Appointment Stage') return 'info';
  if (label === 'Lead Stage' || label === 'Self Book Stage') return 'warning';
  if (label === 'Creative Signal') return 'violet';
  return 'neutral';
};

export const getWhyDriverTone = (directionalDelta) => {
  if (directionalDelta >= 0.05) return 'success';
  if (directionalDelta <= -0.05) return 'danger';
  return 'neutral';
};

export const getComConfidenceTone = (label) => {
  if (label === COM_CONFIDENCE_STABLE) return 'success';
  if (label === COM_CONFIDENCE_DEVELOPING) return 'info';
  if (label === COM_CONFIDENCE_EARLY) return 'warning';
  return 'neutral';
};

export const getFormatTone = (label) => {
  const format = String(label || '').toUpperCase();
  if (VIDEO_FORMAT_TOKENS.some((token) => format.includes(token))) return 'info';
  if (!format || format === 'UNKNOWN') return 'neutral';
  return 'violet';
};

// ─── Dimension columns ─────────────────────────────────────────────────────

export const getDimensionColumns = (viewMode) => {
  if (viewMode === 'theme') {
    return [
      { key: 'themeName', label: 'Theme', minWidth: 220 },
      { key: 'clientCount', label: 'Clients', minWidth: 100, type: 'count' },
      { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
      { key: 'maturityLabel', label: 'Maturity', minWidth: 160 },
    ];
  }
  if (viewMode === 'subtheme') {
    return [
      { key: 'themeName', label: 'Theme', minWidth: 220 },
      { key: 'subthemeName', label: 'Subtheme', minWidth: 220 },
      { key: 'clientCount', label: 'Clients', minWidth: 100, type: 'count' },
      { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
      { key: 'maturityLabel', label: 'Maturity', minWidth: 160 },
    ];
  }
  return [
    { key: 'adName', label: 'Ad', minWidth: 420, sticky: true, stickyLeft: 0, truncate: true, fontSize: 13 },
    { key: 'previewAction', label: 'Preview', minWidth: 110, stickyLeft: 420 },
    { key: 'activeStatusLabel', label: 'Status', minWidth: 120, stickyLeft: 530 },
    { key: 'facebookFormat', label: 'Format', minWidth: 120, stickyLeft: 650 },
    { key: 'stageLabel', label: 'Stage', minWidth: 150, helpText: 'The current evaluation stage for this ad based on the deepest KPI threshold it has reached.' },
    { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
    { key: 'spendShareLabel', label: 'Spend Share', minWidth: 120, helpText: 'Share of spend within this client and vertical in the current date range.' },
    { key: 'primaryKpiLabel', label: 'Primary KPI', minWidth: 150, helpText: 'The most appropriate KPI to judge this ad right now given its maturity.' },
    { key: 'comConfidenceLabel', label: 'CoM Confidence', minWidth: 150, helpText: 'Confidence band for relying on Cost of Marketing based on sale count and deal-value stability.' },
    { key: 'predictedCostOfMarketingLabel', label: 'Pred. CoM', minWidth: 140, helpText: 'Projected Cost of Marketing based on the ad\u2019s current stage and comparable funnel conversion rates.' },
    { key: 'effectiveCostOfMarketingLabel', label: 'Decision CoM', minWidth: 150, helpText: 'Actual Cost of Marketing for mature ads, otherwise the predicted Cost of Marketing used for cross-stage comparison.' },
    { key: 'pausePriorityLabel', label: 'Pause Priority', minWidth: 140, helpText: 'Higher when an ad looks weak, confidence is meaningful, and enough spend is at risk to justify action.' },
    { key: 'buildPriorityLabel', label: 'Build Priority', minWidth: 140, helpText: 'Higher when an ad looks like a winner worth reusing for new creative variations.' },
    { key: 'creativeVerdict', label: 'Creative Verdict', minWidth: 150, helpText: 'Quick read on whether the creative looks winning, promising, mixed, weak, or still too early.' },
    { key: 'overallRecommendation', label: 'Overall Rec.', minWidth: 150, helpText: 'Suggested next action based on the full selected date range.' },
    { key: 'whyLabel', label: 'Why', minWidth: 340, helpText: 'Short explanation for the current recommendation.', truncate: true, fontSize: 12.5 },
  ];
};

export const getRowLabel = (row, viewMode) => {
  if (viewMode === 'theme') return row.themeName || 'Unparsed Theme';
  if (viewMode === 'subtheme') return row.subthemeName || row.themeName || 'Unparsed Subtheme';
  return row.adName || 'Unnamed Ad';
};

// ─── Peer indexing helpers (Fix 1: O(1) peer lookups) ───────────────────────

export const buildPeerIndex = (rows) => {
  const clientVerticalIndex = new Map();
  const agencyVerticalIndex = new Map();
  const clientVerticalSpend = new Map();

  rows.forEach((row) => {
    const cvKey = `${row.clientId}::${row.verticalName}::${row.campaignType}`;
    const avKey = `${row.verticalName}::${row.campaignType}`;

    if (!clientVerticalIndex.has(cvKey)) clientVerticalIndex.set(cvKey, []);
    clientVerticalIndex.get(cvKey).push(row);

    if (!agencyVerticalIndex.has(avKey)) agencyVerticalIndex.set(avKey, []);
    agencyVerticalIndex.get(avKey).push(row);

    clientVerticalSpend.set(cvKey, (clientVerticalSpend.get(cvKey) || 0) + safeNumber(row.spend));
  });

  return { clientVerticalIndex, agencyVerticalIndex, clientVerticalSpend };
};

export const getPeersFromIndex = (row, index, scope) => {
  const cvKey = `${row.clientId}::${row.verticalName}::${row.campaignType}`;
  const avKey = `${row.verticalName}::${row.campaignType}`;
  const key = scope === 'agency' ? avKey : cvKey;
  const sourceIndex = scope === 'agency' ? index.agencyVerticalIndex : index.clientVerticalIndex;
  return (sourceIndex.get(key) || []).filter((peer) => peer.uuid !== row.uuid);
};

export const getSpendShareFromIndex = (row, index) => {
  const cvKey = `${row.clientId}::${row.verticalName}::${row.campaignType}`;
  return safeDiv(row.spend, index.clientVerticalSpend.get(cvKey) || 0);
};
